import { FC, useCallback, useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";
import { Box, CircularProgress, IconButton, Typography } from "@mui/material";

import {
  CopyIcon,
  DownloadIcon,
  ThumbsDownIcon,
  ThumbsUpIcon,
} from "../../../icons";
import { CheckIcon } from "../../../icons/CheckIcon";

import useNotifier from "../../../../hooks/useNotifier";
import {
  htmlWithTableBorders,
  prepareChatMessageMarkdownHtmlForPdf,
  stripCollapsedCoreExpanderLayoutFromExportClone,
} from "../../../../utils";
import { useChatStore } from "../../../../hooks/useChatStore";
import { QuillHelper } from "../../../helpers/quillHelper";
import { formatInstant } from "../../../helpers/dataHelper";
import { IMessage } from "../../../../services";
import { useCapabilities } from "../../../../contexts/permissions/useCapabilities";

interface ChatMessageActionsProps {
  message: IMessage;
  isCrossUserMessage?: boolean;
}

const CHECK_RESET_MS = 2000;

const feedbackIconButtonSx = {
  width: 28,
  height: 28,
  p: 0.5,
  flexShrink: 0,
  "&:hover": {
    backgroundColor: "transparent",
    opacity: 0.7,
  },
};

const messageCreatedAtLabel = (message: IMessage): string => {
  const createdAt = message.created_at;

  if (createdAt != null && String(createdAt).trim() !== "") {
    return formatInstant(createdAt);
  }

  return "";
};

export const ChatMessageActions: FC<ChatMessageActionsProps> = observer(
  ({ message, isCrossUserMessage }) => {
    const chatStore = useChatStore();
    const notifier = useNotifier();
    const { canDownloadAttachments } = useCapabilities();
    const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [copied, setCopied] = useState(false);
    const [feedbackLoading, setFeedbackLoading] = useState<
      null | "up" | "down"
    >(null);
    const [pdfLoading, setPdfLoading] = useState(false);

    const messageDate = messageCreatedAtLabel(message);
    const isStreaming = message?.type === "stream";
    const isEmptyLetterMessage = String(message?.data)
      ?.toLowerCase()
      ?.includes("letter content available.");

    const handleCopyClick = async (e: React.MouseEvent) => {
      e.stopPropagation();

      if (message?.data) {
        try {
          const element = document.getElementById(
            `markdown-preview-${message.id}`,
          );
          const rawHtml = element?.innerHTML;
          const plainText = element?.innerText;

          if (rawHtml != null && plainText != null) {
            const htmlWithBorders = htmlWithTableBorders(rawHtml);
            await navigator.clipboard.write([
              new ClipboardItem({
                "text/html": new Blob([htmlWithBorders], { type: "text/html" }),
                "text/plain": new Blob([plainText], { type: "text/plain" }),
              }),
            ]);
          } else {
            await navigator.clipboard.writeText(message?.data as string);
          }

          handleCopyTimeout();
        } catch (err) {
          try {
            await navigator.clipboard.writeText(message?.data as string);
            handleCopyTimeout();
          } catch {
            /** */
          }
        }
      }
    };

    const handleCopyTimeout = useCallback(() => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }

      setCopied(true);

      copyTimeoutRef.current = setTimeout(() => {
        setCopied(false);
        copyTimeoutRef.current = null;
      }, CHECK_RESET_MS);
    }, []);

    useEffect(() => {
      return () => {
        if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
      };
    }, []);

    const onThumbsActionClick = async (
      e: React.MouseEvent,
      action: "up" | "down",
    ) => {
      try {
        setFeedbackLoading(action);
        const isSuccess = await chatStore.setChatMessageFeedback(
          message.id as string,
          action,
          message?.feedback_rating === action,
        );

        if (isSuccess) {
          notifier.showSuccess("Feedback submitted successfully");
          setFeedbackLoading(null);
          return;
        }

        notifier.showError("Failed to submit feedback");
      } catch {
        notifier.showError("Failed to submit feedback");
      } finally {
        setFeedbackLoading(null);
      }
    };

    const handleDownloadClick = async (e: React.MouseEvent) => {
      e.stopPropagation();

      if (!message?.data || pdfLoading) return;

      const preview = document.getElementById(`markdown-preview-${message.id}`);
      if (!preview) {
        notifier.showError("Could not find message preview for PDF export.");
        return;
      }

      // Clone so we do not mutate the live DOM; open native <details> so export HTML matches expanded view.
      const clone = preview.cloneNode(true) as HTMLElement;
      clone.querySelectorAll("details:not([open])").forEach((el) => {
        el.setAttribute("open", "");
      });
      stripCollapsedCoreExpanderLayoutFromExportClone(clone);
      const rawHtml = clone.innerHTML?.trim();

      if (!rawHtml) {
        notifier.showError("Could not find message preview for PDF export.");
        return;
      }

      setPdfLoading(true);
      try {
        const htmlForPdf = prepareChatMessageMarkdownHtmlForPdf(rawHtml);
        const safeId = String(message.id ?? "export").replace(
          /[<>:"/\\|?*]/g,
          "_",
        );
        QuillHelper.downloadPdfFromPreparedHtml(
          htmlForPdf,
          `chat_message_${safeId}`,
        );
      } catch {
        notifier.showError("Failed to generate PDF.");
      } finally {
        setPdfLoading(false);
      }
    };

    return (
      <Box
        data-message-actions
        sx={{
          opacity: 0,
          transition: "opacity 200ms ease-in-out",
          display: "flex",
          gap: "5px",
          paddingTop: 0.5,
          paddingBottom: 0.5,
          "& .MuiIconButton-root": {
            "&:hover": {
              opacity: 0.7,
            },
          },
        }}
      >
        {isCrossUserMessage || isStreaming ? null : (
          <>
            <IconButton
              aria-label="Thumbs up"
              onClick={(e) => onThumbsActionClick(e, "up")}
              disabled={feedbackLoading === "up"}
              sx={feedbackIconButtonSx}
            >
              {feedbackLoading === "up" ? (
                <CircularProgress size={16} />
              ) : (
                <ThumbsUpIcon
                  size={20}
                  filled={message?.feedback_rating === "up"}
                />
              )}
            </IconButton>

            <IconButton
              aria-label="Thumbs down"
              onClick={(e) => onThumbsActionClick(e, "down")}
              disabled={feedbackLoading === "down"}
              sx={feedbackIconButtonSx}
            >
              {feedbackLoading === "down" ? (
                <CircularProgress size={16} />
              ) : (
                <ThumbsDownIcon
                  size={20}
                  filled={message?.feedback_rating === "down"}
                />
              )}
            </IconButton>
          </>
        )}

        {
          // Nothing to copy/download
          !message?.data || isEmptyLetterMessage ? null : (
            <>
              <IconButton
                aria-label="Copy"
                onClick={handleCopyClick}
                title={copied ? "Copied!" : "Copy to clipboard"}
                sx={feedbackIconButtonSx}
              >
                {copied ? (
                  <CheckIcon size={20} className="text-green-600" />
                ) : (
                  <CopyIcon size={20} className="text-gray-600" />
                )}
              </IconButton>

              {isStreaming || isEmptyLetterMessage || !canDownloadAttachments ? null : (
                <IconButton
                  aria-label="Download as PDF"
                  onClick={handleDownloadClick}
                  disabled={pdfLoading}
                  title="Download as PDF"
                  sx={feedbackIconButtonSx}
                >
                  {pdfLoading ? (
                    <CircularProgress size={16} />
                  ) : (
                    <DownloadIcon size={20} className="text-gray-600" />
                  )}
                </IconButton>
              )}

              {messageDate ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    width: "100%",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {messageDate}
                  </Typography>
                </Box>
              ) : null}
            </>
          )
        }
      </Box>
    );
  },
);
